import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import {
  AppointmentDto,
  AppointmentStatus,
  DiscussionDto,
  MessageDto,
  PropertyApplicationDto,
  PropertyApplicationStatus,
  PropertyDto,
} from '@workspace/data-types';
import {
  ChatPendingAction,
  DecoratedMessage,
  DiscussionChatCoreComponent,
} from '@workspace/ng-core-features';
import { UserStore } from '@workspace/ng-data-access';
import { CanDeleteMessagePipe } from '../../pipes/can-delete-message.pipe';
import {
  DiscussionProfileDialogComponent,
  DiscussionProfileDialogData,
} from '../discussion-profile-dialog/discussion-profile-dialog.component';

@Component({
  selector: 'etxe-discussion-chat',
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    DiscussionChatCoreComponent,
  ],
  providers: [CanDeleteMessagePipe],
  host: {
    class: 'flex-1 flex flex-col min-w-0 min-h-0 h-full',
  },
  templateUrl: './discussion-chat.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionChatComponent {
  private readonly userStore = inject(UserStore);
  private readonly canDeleteMessagePipe = inject(CanDeleteMessagePipe);
  private readonly dialog = inject(MatDialog);

  activeDiscussion = input<DiscussionDto | null>(null);

  readonly otherParticipant = computed(() => {
    const active = this.activeDiscussion();
    if (!active) return null;
    const currentUserId = this.userStore.current()?._id?.toString();
    if (!currentUserId) return null;
    return (
      active.participants?.find((p) => p.participant && p.participant.toString() !== currentUserId) ?? null
    );
  });

  readonly isOtherParticipantAgent = computed(() => {
    return this.otherParticipant()?.participantType === 'agent';
  });

  readonly participantAvatarUrl = computed(() => {
    return this.otherParticipant()?.avatarUrl ?? null;
  });
  decoratedMessages = input<DecoratedMessage[]>([]);
  isLoadingMessages = input<boolean>(false);
  isLoadingMore = input<boolean>(false);
  timeFormat = input<string>('HH:mm');

  pinnedMessage = input<MessageDto | null>(null);
  appointment = input<AppointmentDto | null>(null);
  application = input<PropertyApplicationDto | null>(null);
  isOwner = input<boolean>(false);
  property = input<PropertyDto | null>(null);
  pendingAction = input<ChatPendingAction | null>(null);
  pendingActions = input<ChatPendingAction[]>([]);

  actionClick = output<ChatPendingAction>();
  sendMessage = output<{ text: string; replyToId?: string }>();

  readonly resolvedPendingActions = computed<ChatPendingAction[]>(() => {
    const actions: ChatPendingAction[] = [...this.pendingActions()];
    const customAction = this.pendingAction();
    if (customAction) {
      actions.push(customAction);
    }

    const app = this.application();
    if (app?.status === PropertyApplicationStatus.EXPIRED) {
      return actions;
    }

    const appt = this.appointment();
    const isOwner = this.isOwner();

    // Check recent messages for visit actions to provide instant UI reactivity
    const msgs = this.decoratedMessages();
    const lastVisitMsg = [...msgs]
      .reverse()
      .find((m) => m.actionType?.startsWith('property_visit') || (m.type === 'action' && m.actionType));

    let effectiveStatus: AppointmentStatus | string | null = null;
    if (lastVisitMsg?.actionType) {
      if (lastVisitMsg.actionType === 'property_visit_proposed') {
        effectiveStatus = AppointmentStatus.PROPOSED;
      } else if (lastVisitMsg.actionType === 'property_visit_rescheduled') {
        effectiveStatus = AppointmentStatus.RESCHEDULED;
      } else if (
        lastVisitMsg.actionType === 'property_visit_confirmed' ||
        lastVisitMsg.actionType === 'property_visit_completed'
      ) {
        effectiveStatus = AppointmentStatus.CONFIRMED;
      }
    }

    // Fall back to appointment status if no recent visit action message found
    if (!effectiveStatus && appt?.status) {
      effectiveStatus = appt.status.toLowerCase();
    }

    const deadline =
      app?.pendingAction?.deadline ||
      (appt?.updatedAt ? new Date(new Date(appt.updatedAt).getTime() + 24 * 60 * 60 * 1000) : null) ||
      (lastVisitMsg?.createdAt
        ? new Date(new Date(lastVisitMsg.createdAt).getTime() + 24 * 60 * 60 * 1000)
        : null);

    if (!isOwner && effectiveStatus === AppointmentStatus.PROPOSED) {
      actions.push({
        type: 'property_visit',
        title: 'Créneaux de visite proposés',
        message: 'Le propriétaire vous a proposé des créneaux de visite pour ce bien.',
        icon: 'event',
        actionLabel: 'Choisir un créneau',
        severity: 'primary',
        deadline: deadline || undefined,
        data: appt,
      });
    } else if (isOwner && effectiveStatus === AppointmentStatus.RESCHEDULED) {
      actions.push({
        type: 'property_visit',
        title: 'Nouvelle date demandée',
        message: "L'agent a demandé d'autres créneaux pour visiter le bien.",
        icon: 'event_repeat',
        actionLabel: 'Proposer de nouveaux créneaux',
        severity: 'warning',
        deadline: deadline || undefined,
        data: appt,
      });
    }

    return actions;
  });
  copyMessageText = output<MessageDto>();
  pinMessage = output<MessageDto>();
  infoMessage = output<MessageDto>();
  deleteMessage = output<MessageDto>();
  scrolledToTop = output<void>();
  readActiveDiscussion = output<void>();
  unpinMessage = output<void>();
  retryMessage = output<MessageDto>();

  readonly chatFeed = viewChild<DiscussionChatCoreComponent>('chatFeed');

  readonly replyingTo = signal<MessageDto | null>(null);

  showDeletePipe = (msg: MessageDto, idx: number, list: DecoratedMessage[]) => {
    return this.canDeleteMessagePipe.transform(msg, idx, list);
  };

  isMe = (msg: MessageDto): boolean => {
    const userId = this.userStore.current()?._id;
    if (!userId) return false;
    return msg.sender.toString() === userId.toString();
  };

  getParticipantName = (discussion: DiscussionDto): string => {
    const userId = this.userStore.current()?._id;
    const other = discussion.participants.find(
      (p) => userId && p.participant.toString() !== userId.toString(),
    );
    if (other?.firstName) {
      return other.firstName;
    }
    if (other?.participantType === 'owner' || other?.participantType === 'seller') {
      return 'Propriétaire vendeur';
    }
    return 'Utilisateur';
  };

  getParticipantInitial = (discussion: DiscussionDto): string => {
    const name = this.getParticipantName(discussion);
    return name.charAt(0).toUpperCase();
  };

  openParticipantProfileModal(): void {
    const other = this.otherParticipant();
    if (!other?.participant) return;

    const isAgent = other.participantType === 'agent';
    const discussion = this.activeDiscussion();
    this.dialog.open<DiscussionProfileDialogComponent, DiscussionProfileDialogData>(
      DiscussionProfileDialogComponent,
      {
        width: '100%',
        maxWidth: isAgent ? '760px' : '480px',
        maxHeight: '90vh',
        autoFocus: false,
        data: {
          participantId: other.participant,
          participantType: other.participantType,
          participantName: other.firstName || (discussion ? this.getParticipantName(discussion) : ''),
          participantAvatarUrl: other.avatarUrl,
          propertyId: discussion?.context?.property ? discussion.context.property.toString() : undefined,
        },
      },
    );
  }

  onActionMessageClick(msg: MessageDto): void {
    this.actionClick.emit({
      type: 'property_visit',
      message: msg.text,
      actionLabel: 'Choisir un créneau',
      data: msg,
    });
  }

  scrollToBottom() {
    this.chatFeed()?.scrollToBottom();
  }
}
